import { AnyAction } from 'redux';
import { put, select, takeEvery } from 'redux-saga/effects';

import { actions } from '../../../../components/conversation/actions/conversation';
import { notificationActions } from '../../../../components/notifications/actions/notification';
import { sameCsid } from '../../../../components/telephony/utils/sameCsid';
import { twilioActions } from '../actions/twilioActions';

const { notificationAdd } = notificationActions;

const {
  twilioDisconnectCall,
  twilioConnectCallSucceeded,
  twilioMakeCallFailed,
  twilioMakeInternalCallFailed,
  twilioMakeCallbackFailed,
  twilioConnectCallFailed,
} = twilioActions;

const { conversationGetSucceeded } = actions;

// Saga
export const twilio = [
  takeEvery(conversationGetSucceeded.toString(), conversationGetSucceededGen),
  takeEvery(
    twilioConnectCallSucceeded.toString(),
    twilioConnectCallSucceededGen,
  ),
  takeEvery(twilioMakeCallFailed.toString(), twilioMakeCallFailedGen),
  takeEvery(twilioMakeInternalCallFailed.toString(), twilioMakeCallFailedGen),
  takeEvery(twilioMakeCallbackFailed.toString(), twilioMakeCallFailedGen),
  takeEvery(twilioConnectCallFailed.toString(), twilioMakeCallFailedGen),
];

function* twilioMakeCallFailedGen(action: AnyAction) {
  // `missing_region` and `missing_csid` are pre-flight failures already handled
  // in `voiceSdk/middleware/commandExecutor.ts`:
  // - `requireTokenRegion()` / `rejectMissingTokenRegion()` show a specific
  //   "Telephony issue" notification when the call cannot be placed in a valid
  //   region.
  // - `readCsidFromAction()` / `rejectMissingCsid()` show a specific
  //   "Telephony issue" notification when a required conversation ID is missing.
  //
  // The matching `*Failed` actions still flow through this saga so reducers can
  // settle state, but we must skip the generic "Could not start the call"
  // banner here to avoid showing the agent two notifications for the same
  // problem.
  // TODO https://dixa-product.atlassian.net/browse/IO-4100
  if (
    action.payload?.reason === 'missing_region' ||
    action.payload?.reason === 'missing_csid'
  ) {
    return;
  }

  const option = {
    title: 'Could not start the call',
    text: 'Please try again. If the problem persists, refresh the page or contact support.',
    className: 'warning',
    icon: 'i-info',
  };
  yield put(notificationAdd(option));
}

function* conversationGetSucceededGen(action: AnyAction) {
  // Look at the conversation type and if it closed.
  // if it is pstnphone or callback we should lookup our
  // current conversation and see if the csid is the same.
  const conversation = Object.assign(
    {},
    action.payload.data.history.main_conversation,
  );
  if (conversation.is_closed !== true) return;
  if (
    conversation.initial_channel !== 'pstnphone' &&
    conversation.initial_channel !== 'callback'
  ) {
    return;
  }

  const telephonyState = yield select((state) => state.telephony);

  if (sameCsid(telephonyState.currentCall?.csid, conversation.id)) {
    // The conversation has been closed - we may have missed the WS event.
    yield put(twilioDisconnectCall({ csid: conversation.id }));
  }
}

function* twilioConnectCallSucceededGen(action: AnyAction) {
  const conversationsState = yield select((state) => state.conversations);

  for (const metaConversation of conversationsState.metaConversations) {
    if (
      metaConversation.conversation.status === 'closed' &&
      sameCsid(metaConversation.conversation.id, action.payload.csid)
    ) {
      yield put(twilioDisconnectCall({ csid: action.payload.csid }));
    }
  }
}
